/*
 * Page access — the frontend half of the model the backend enforces.
 *
 * A permission is the name of a left-nav page (lowercased, words joined with
 * underscores) and what you may do with it is four flags: create, read, update,
 * delete. The backend ships the whole grid on /me as `pageAccess`:
 *
 *     { sales_crm: ["create", "read", "update"], home: ["read"], ... }
 *
 * The sidebar, the route guard and every in-page button ask this module the same
 * question the server asks itself, so what the UI offers and what the API allows
 * cannot drift apart. Hiding a button is a courtesy; the server is the authority.
 */

// Left-nav label -> page name. The nav renders labels; permissions are pages.
const LABEL_TO_PAGE = {
  "Home": "home",
  "Notifications": "notifications",
  "Sales CRM": "sales_crm",
  "Sales CRM ✨": "sales_crm_board",
  // Split 2026-10-07 from the old single "Analytics & Perf" page — Sales
  // Pipeline kept its route, Sales Performance is new. See the matching split
  // in Backend/EnterpriseCore/pages.py.
  "Sales Pipeline": "sales_pipeline",
  "Sales Performance": "sales_performance",
  "SDR Onboarding": "junior_onboarding",
  "AE Onboarding": "sales_onboarding",
  "Playbook": "playbook",
  "RevOps": "revops",
  // Same grant as RevOps — V2 is a separate screen, not a separate permission.
  "RevOps V2": "revops",
  "Projects": "projects",
  "Project Management": "project_management",
  "Delay Management": "delay_management",
  "HRMS": "hrms",
  "Leave Requests": "leave_requests",
  "HR Actions": null,               // a group heading, not a destination
  "Send Offer Letter": "send_offer_letter",
  // Its own grant-only page: only superusers and people a superuser grants it
  // to. Not tied to Send Offer Letter, which every full-timer gets.
  "IDs Waiting": "ids_waiting",
  "Send Certificate": "send_certificate",
  "Deactivate Employee": "deactivate_employee",
  "Finance": "finance",
  "P&L": "pnl",
  "DevOps": "devops",
  "Employee Register": "employee_register",
  "Apply Leave": "apply_leave",
  // Payslips, Bank details and Expense claims are tabs of one screen; every employee has all three pages, so the
  // entry follows Payslips. Each tab is still judged by its own page on the server.
  "My Pay & Claims": "payslips",
  "Password Management": "password_management",
  // Documents fans out to one sidebar link per project — and, since
  // 2026-10-07, one page-access gate per project too. Granting one no longer
  // opens the other four.
  "How we work": "documentation_company",
  "Intranet": "documentation_intranet",
  "InterviewGod": "documentation_interviewgod",
  "Banao Technologies": "documentation_banao",
  "Vikaas": "documentation_vikaas",
  "Vidya": "documentation_vidya",
  "Vibe testing (Avalok)": "vibe_testing_avalok",
  "Monitoring": "monitoring",
  "Logs": "monitoring",
  "Growth Roadmap": "growth_roadmap",
  "Integrations": "integrations",
  // Exactly "AXON API" in all three places — this label, the sidebar item in
  // App.jsx, and nav_label in Backend/EnterpriseCore/pages.py. See
  // Backend/EnterpriseCore/test_nav_labels_match.py for why.
  "AXON API": "axon_api",
  "Plan and Plugins": "plan_and_plugins",
  // Exactly "Suggested Tasks" in all three places, like "AXON API" above.
  "Suggested Tasks": "proposals",
  "Admin Panel": "admin_panel",
};

// Route prefix -> page name, longest match wins.
const ROUTE_TO_PAGE = [
  ["/lms/analytics", "sales_pipeline"],
  ["/sales-performance", "sales_performance"],
  ["/lms", "sales_crm"],
  ["/crm", "sales_crm_board"],
  // Swapped 2026-08-11 — the URL now matches the nav label on both.
  ["/junior-onboarding", "junior_onboarding"],
  ["/sales-onboarding", "sales_onboarding"],
  ["/playbook", "playbook"],
  // Longer prefix wins over "/revops" so V2 is not gated as the wrong page.
  ["/revops-v2", "revops"],
  ["/revops", "revops"],
  ["/projects", "projects"],
  ["/projects/management", "project_management"],
  // The path this page shipped under for its first week, before the rename.
  ["/projects/sanity", "project_management"],
  // Projects (Old) and Project Management (Old) retired 2026-08-14 — routes
  // no longer map to anything, so a stale link now fails closed via the
  // "unmapped route" branch in canAccessRoute() below, same as /project/sanity.
  ["/delays", "delay_management"],
  ["/hrms", "hrms"],
  ["/leave-requests", "leave_requests"],
  ["/leave/apply", "apply_leave"],
  ["/leave", "apply_leave"],
  ["/Onboard/Send_Offer", "send_offer_letter"],
  ["/joining-reviews", "ids_waiting"],
  ["/send-certificate", "send_certificate"],
  ["/deactivate", "deactivate_employee"],
  ["/payments", "finance"],
  ["/finance", "finance"],
  ["/pnl", "pnl"],
  ["/devops", "devops"],
  ["/employee-register", "employee_register"],
  // Old misspelled alias AppScreens still renders; without this it failed closed.
  ["/employee-registrar", "employee_register"],
  ["/Bankdetails", "bank_details"],
  ["/bank", "bank_details"],
  ["/payslips", "payslips"],
  ["/change-password", "password_management"],
  ["/admin/change-password", "password_management"],
  // Documents (Old) is gone; its old links redirect to the readable documents,
  // so the route has to pass the gate for everyone to reach that redirect.
  ["/docs-old", "documentation_company"],
  // Project is a path segment now (/docs/<project>/...), each its own page —
  // longest-prefix-wins means these five outrank the bare "/docs" fallback
  // below for any new-style link. That fallback exists only for a link saved
  // before this scheme existed (no project segment at all, e.g. a bare
  // "/docs/Company/..." or an old "?project=" query link) — DocsPortalScreen
  // resolves those the same way, to Intranet, so the gate here has to agree.
  ["/docs/company", "documentation_company"],
  ["/docs/intranet", "documentation_intranet"],
  ["/docs/interviewgod", "documentation_interviewgod"],
  ["/docs/banao", "documentation_banao"],
  ["/docs/vikaas", "documentation_vikaas"],
  ["/docs/vidya", "documentation_vidya"],
  ["/docs", "documentation_intranet"],
  ["/policies", "company_policies"],
  ["/vibe-testing", "vibe_testing_avalok"],
  ["/monitoring", "monitoring"],
  ["/logs", "monitoring"],
  ["/growth-roadmap", "growth_roadmap"],
  ["/integrations", "integrations"],
  ["/axon-api", "axon_api"],
  ["/admin-panel", "admin_panel"],
  ["/company-admin", "admin_panel"],
  ["/plan-and-plugins", "plan_and_plugins"],
  ["/proposals", "proposals"],
  ["/notifications", "notifications"],
  ["/home", "home"],
  ["/profile", "home"],
  ["/onboarding", "home"],
];

export function pageForRoute(path) {
  const clean = (path || "").split("?")[0];
  let best = null;
  let bestLength = -1;
  for (const [prefix, page] of ROUTE_TO_PAGE) {
    if (clean.startsWith(prefix) && prefix.length > bestLength) {
      best = page;
      bestLength = prefix.length;
    }
  }
  return best;
}

function grid(me) {
  return me?.pageAccess || {};
}

/** Can this user perform `action` on `page`? */
function can(me, page, action = "read") {
  if (!page) return false;
  const actions = grid(me)[page];
  return Array.isArray(actions) && actions.includes(action);
}

export const canRead = (me, page) => can(me, page, "read");
export const canCreate = (me, page) => can(me, page, "create");
export const canUpdate = (me, page) => can(me, page, "update");
export const canDelete = (me, page) => can(me, page, "delete");

/** Does this person run the company? The same test the server makes (Platform/core/company_admin.py):
 * holding update on the admin panel. The operator inside their own company holds it through the grid. */
export const runsCompany = (me) => can(me, "admin_panel", "update");

/**
 * The product a page belongs to, when the company has switched that product off
 * (plugin platform). null when the page's product is on, or the page is platform.
 * The server sends both maps on /me: pagePlugins {page: product} and plugins
 * {product: on?}. With every product on (today) this is always null.
 */
export function switchedOffProduct(me, page) {
  if (!page) return null;
  const code = (me?.pagePlugins || {})[page];
  if (!code) return null;
  return (me?.plugins || {})[code] === false ? code : null;
}

/** Should this nav label be rendered? */
export function canSeeLabel(me, label) {
  const page = LABEL_TO_PAGE[label];
  if (page === null) return true;      // group heading; its children decide
  if (page === undefined) return !!me?.user?.isSuperuser; // unknown label — fail closed
  if (switchedOffProduct(me, page)) return false; // not part of this company's plan
  return canRead(me, page);
}

/** Should this route render, or bounce to Home? A switched-off product's route
 * renders (it shows "Not part of your plan"), so the person learns why. */
export function canAccessRoute(me, path) {
  // The audit portal is opened by an auditor engagement, not by a page grant (S9).
  // A superuser is not an auditor: they cannot read the trail.
  if ((path || "").startsWith("/audit")) return !!me?.isAuditor;
  // The operator console is for the platform's own staff, never a page grant: the server decides what it returns.
  if ((path || "").startsWith("/platform")) return !!me?.user?.isOperator;
  const page = pageForRoute(path);
  if (switchedOffProduct(me, page)) return true;
  if (!page) return false;             // unmapped route — fail closed
  return canRead(me, page);
}

/** True once the grid has arrived, so the UI can avoid a flash of empty nav. */
export function hasPageAccess(me) {
  return !!me && typeof me.pageAccess === "object" && me.pageAccess !== null;
}
